import { UPPY_MAX_FILE_SIZE_BYTES } from 'constants/image';
import * as Sentry from '@sentry/react';
import Uppy from '@uppy/core';
import * as DE from '@uppy/locales/lib/de_DE';
import * as EN from '@uppy/locales/lib/en_US';
import * as ES from '@uppy/locales/lib/es_ES';
import * as FR from '@uppy/locales/lib/fr_FR';
import * as IT from '@uppy/locales/lib/it_IT';
import * as NL from '@uppy/locales/lib/nl_NL';
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import Tus from '@uppy/tus';
import Button from 'components/Qds/Button';
import Alert from 'components/ui/Alert';
import { useTranslation } from 'hooks/useTranslation';
import md5 from 'md5';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import React from 'react';
import config from 'config';
import styles from './ImageUpload.module.scss';
import ImageUploadSpot from './ImageUploadSpot';
import type { TypeChecking } from '@uppy/core';
import type { Image } from 'types/Utilities';

export type StepImageUpload = {
  title?: string;
  description?: string;
  placeholder?: Image;
};

type Props = {
  blockId: string;
  title?: string;
  subtitle?: string;
  uploads?: {
    title?: string;
    subtitle?: string;
    uploads?: { items: StepImageUpload[] };
  };
  junoId?: string;
  hideTitles?: boolean;
};

const ImageUpload = ({
  junoId,
  blockId,
  uploads,
  hideTitles,
}: Props): JSX.Element | null => {
  const { locale, t } = useTranslation(true);
  const [uppy, setUppy] = useState<Uppy.Uppy<TypeChecking> | null>(null);
  const [isUppySubmitted, setIsUppySubmitted] = useState(false);
  const [isUppyFailed, setIsUppyFailed] = useState(false);
  const [hasImages, setHasImages] = useState(false);
  const { query } = useRouter();
  const junoid = junoId || query.junoid || null;

  useEffect(() => {
    const uppyLocale = ((locale) => {
      switch (locale) {
        case 'en':
          return EN;
        case 'de':
          return DE;
        case 'nl':
          return NL;
        case 'fr':
          return FR;
        case 'es':
          return ES;
        case 'it':
          return IT;
      }
    })(locale);

    const uppy = Uppy({
      meta: {
        source: '',
        junoid,
      },
      restrictions: {
        maxFileSize: UPPY_MAX_FILE_SIZE_BYTES,
      },
      locale: uppyLocale,
    })
      .use(Tus, {
        id: 'Tus',
        endpoint: 'https://patients.qunomedical.com/files/',
      })
      .use(ThumbnailGenerator, {
        thumbnailWidth: 350,
      });

    const { UPPY_SALT } = process.env;
    if (UPPY_SALT) {
      const securityToken = md5(
        `${UPPY_SALT.slice(0, 5)}${junoid}${UPPY_SALT}`,
      );

      uppy.setMeta({ securityToken });
    }

    setUppy(uppy);

    uppy.on('file-added', () => {
      if (uppy.getFiles().length >= 1) {
        return setHasImages(true);
      }

      setHasImages(false);
    });

    uppy.on('file-removed', () => {
      if (!uppy.getFiles().length) {
        setHasImages(false);
      }
    });
  }, []);

  // -- add to form data
  const submitPhotos = async (): Promise<void> => {
    const response = await uppy?.upload();
    if (response && response?.successful?.length >= 1) {
      // eslint-disable-next-line
      // @ts-ignore
      const uppyFiles: Uppy.State['files'] = uppy?.getState().files;
      const payload = {
        successful: Object.keys(uppyFiles).map((key) => {
          return uppyFiles?.[key];
        }),
      };

      try {
        const response = await fetch(
          `${config.defaults.qunoApi}/v1/customer/picture`,
          {
            method: 'POST',
            body: JSON.stringify({ uppyResult: payload }),
          },
        );

        if (response.status === 200) {
          setIsUppySubmitted(true);
          uppy?.cancelAll();
          uppy?.reset();
          return;
        }

        setIsUppyFailed(true);
        uppy?.cancelAll();
        uppy?.reset();

        throw new Error(`Can't upload images via uppy content block`);
      } catch (error) {
        const { message, stack } = error as Error;
        Sentry.captureException(
          new Error("Can't upload images via uppy content block"),
          {
            level: 'fatal' as Sentry.SeverityLevel,
            extra: {
              error: message,
              stack,
            },
          },
        );
      }
    }
  };

  if (!uppy || !junoid) {
    return null;
  }

  return (
    <div id={blockId}>
      {isUppySubmitted && (
        <Alert
          text={t('imageUpload.success')}
          type="success"
          vertical={false}
        />
      )}
      {isUppyFailed && (
        <Alert
          text="There was an error when uploading the images."
          type="error"
          vertical={false}
        />
      )}
      {!isUppySubmitted && uploads && (
        <>
          <h2 className={styles.photoUploadHeadline}>
            {uploads.title || t('photoUpload')}
          </h2>
          <p>{uploads.subtitle || t('photoUpload.intro')}</p>
          <ul className={styles.layout}>
            {uploads.uploads &&
              uploads.uploads.items.map((spot, i) => (
                <li key={i} className={styles.uploadSpots}>
                  <ImageUploadSpot
                    spot={spot}
                    uppy={uppy}
                    index={i}
                    hideTitles={hideTitles}
                  />
                </li>
              ))}
          </ul>
          <input type="hidden" name="uploaded_files" />

          <div style={{ marginBlock: '1.25rem' }}>
            <Button
              fullWidth="mobile"
              disabled={!hasImages}
              onClick={submitPhotos}
            >
              {t('Submit photos')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ImageUpload;
